import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type UtilityMode = "high-pass" | "offset" | "minimum" | "maximum" | "clouds" | "difference-clouds";

export interface UtilityParams {
  mode: UtilityMode;
  radius: number;
  offsetX: number;
  offsetY: number;
  fill: "wrap" | "repeat" | "background";
  bgColor: { r: number; g: number; b: number };
  seed: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

// ---------------------------------------------------------------------------
// Gaussian Blur (for High Pass)
// ---------------------------------------------------------------------------

function buildGaussianKernel(sigma: number): Float32Array {
  if (!(sigma > 0)) return new Float32Array([1]);
  const half = Math.max(1, Math.ceil(3 * sigma));
  const size = 2 * half + 1;
  const kernel = new Float32Array(size);
  const twoSigmaSq = 2 * sigma * sigma;
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - half;
    const v = Math.exp(-(x * x) / twoSigmaSq);
    kernel[i] = v;
    sum += v;
  }
  const inv = 1 / sum;
  for (let i = 0; i < size; i++) kernel[i] = kernel[i]! * inv;
  return kernel;
}

function gaussianBlur(
  src: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
): Uint8ClampedArray {
  if (!(radius > 0) || w === 0 || h === 0) return new Uint8ClampedArray(src);
  const sigma = radius / 3;
  const kernel = buildGaussianKernel(sigma);
  if (kernel.length === 1) return new Uint8ClampedArray(src);

  const n = w * h;
  const pre = new Float32Array(w * h * 4);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3]! / 255;
    pre[o] = src[o]! * a;
    pre[o + 1] = src[o + 1]! * a;
    pre[o + 2] = src[o + 2]! * a;
    pre[o + 3] = src[o + 3]!;
  }

  const tmp = new Float32Array(w * h * 4);
  const half = (kernel.length - 1) >> 1;

  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      for (let k = 0; k < kernel.length; k++) {
        let sx = x + k - half;
        if (sx < 0) sx = 0;
        else if (sx >= w) sx = w - 1;
        const o = (row + sx) * 4;
        const wk = kernel[k]!;
        r += pre[o]! * wk;
        g += pre[o + 1]! * wk;
        b += pre[o + 2]! * wk;
        a += pre[o + 3]! * wk;
      }
      const o = (row + x) * 4;
      tmp[o] = r; tmp[o + 1] = g; tmp[o + 2] = b; tmp[o + 3] = a;
    }
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      for (let k = 0; k < kernel.length; k++) {
        let sy = y + k - half;
        if (sy < 0) sy = 0;
        else if (sy >= h) sy = h - 1;
        const o = (sy * w + x) * 4;
        const wk = kernel[k]!;
        r += tmp[o]! * wk;
        g += tmp[o + 1]! * wk;
        b += tmp[o + 2]! * wk;
        a += tmp[o + 3]! * wk;
      }
      const o = (y * w + x) * 4;
      pre[o] = r; pre[o + 1] = g; pre[o + 2] = b; pre[o + 3] = a;
    }
  }

  const out = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = pre[o + 3]!;
    if (a <= 0) { out[o] = 0; out[o + 1] = 0; out[o + 2] = 0; out[o + 3] = 0; continue; }
    const inv = 255 / a;
    out[o] = Math.round(pre[o]! * inv);
    out[o + 1] = Math.round(pre[o + 1]! * inv);
    out[o + 2] = Math.round(pre[o + 2]! * inv);
    out[o + 3] = Math.round(a);
  }
  return out;
}

// ---------------------------------------------------------------------------
// High Pass
// ---------------------------------------------------------------------------

function applyHighPass(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
): void {
  const blurred = gaussianBlur(src, w, h, radius);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    dst[o] = clamp8(Math.round(src[o]! - blurred[o]! + 128));
    dst[o + 1] = clamp8(Math.round(src[o + 1]! - blurred[o + 1]! + 128));
    dst[o + 2] = clamp8(Math.round(src[o + 2]! - blurred[o + 2]! + 128));
    dst[o + 3] = src[o + 3]!;
  }
}

// ---------------------------------------------------------------------------
// Offset
// ---------------------------------------------------------------------------

function applyOffset(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  offsetX: number,
  offsetY: number,
  fill: "wrap" | "repeat" | "background",
  bgColor: { r: number; g: number; b: number },
): void {
  const n = w * h;
  if (fill === "wrap") {
    for (let i = 0; i < n; i++) {
      const y = Math.floor(i / w);
      const x = i % w;
      let sx = (x - offsetX) % w;
      if (sx < 0) sx += w;
      let sy = (y - offsetY) % h;
      if (sy < 0) sy += h;
      const o = i * 4;
      const so = (sy * w + sx) * 4;
      dst[o] = src[so]!;
      dst[o + 1] = src[so + 1]!;
      dst[o + 2] = src[so + 2]!;
      dst[o + 3] = src[o + 3]!;
    }
  } else if (fill === "repeat") {
    for (let i = 0; i < n; i++) {
      const y = Math.floor(i / w);
      const x = i % w;
      let sx = x - offsetX;
      let sy = y - offsetY;
      if (sx < 0) sx = 0;
      else if (sx >= w) sx = w - 1;
      if (sy < 0) sy = 0;
      else if (sy >= h) sy = h - 1;
      const o = i * 4;
      const so = (sy * w + sx) * 4;
      dst[o] = src[so]!;
      dst[o + 1] = src[so + 1]!;
      dst[o + 2] = src[so + 2]!;
      dst[o + 3] = src[o + 3]!;
    }
  } else {
    for (let i = 0; i < n; i++) {
      const y = Math.floor(i / w);
      const x = i % w;
      const sx = x - offsetX;
      const sy = y - offsetY;
      const o = i * 4;
      if (sx < 0 || sx >= w || sy < 0 || sy >= h) {
        dst[o] = bgColor.r;
        dst[o + 1] = bgColor.g;
        dst[o + 2] = bgColor.b;
        dst[o + 3] = src[o + 3]!;
      } else {
        const so = (sy * w + sx) * 4;
        dst[o] = src[so]!;
        dst[o + 1] = src[so + 1]!;
        dst[o + 2] = src[so + 2]!;
        dst[o + 3] = src[o + 3]!;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Minimum / Maximum
// ---------------------------------------------------------------------------

function applyMinMax(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
  isMin: boolean,
): void {
  const r = Math.round(radius);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const cy = Math.floor(i / w);
    const cx = i % w;
    const y0 = Math.max(0, cy - r);
    const y1 = Math.min(h - 1, cy + r);
    const x0 = Math.max(0, cx - r);
    const x1 = Math.min(w - 1, cx + r);
    let bestR = src[(cy * w + cx) * 4]!;
    let bestG = src[(cy * w + cx) * 4 + 1]!;
    let bestB = src[(cy * w + cx) * 4 + 2]!;
    if (isMin) {
      for (let y = y0; y <= y1; y++) {
        const row = y * w;
        for (let x = x0; x <= x1; x++) {
          const o = (row + x) * 4;
          if (src[o]! < bestR) bestR = src[o]!;
          if (src[o + 1]! < bestG) bestG = src[o + 1]!;
          if (src[o + 2]! < bestB) bestB = src[o + 2]!;
        }
      }
    } else {
      for (let y = y0; y <= y1; y++) {
        const row = y * w;
        for (let x = x0; x <= x1; x++) {
          const o = (row + x) * 4;
          if (src[o]! > bestR) bestR = src[o]!;
          if (src[o + 1]! > bestG) bestG = src[o + 1]!;
          if (src[o + 2]! > bestB) bestB = src[o + 2]!;
        }
      }
    }
    const o = i * 4;
    dst[o] = bestR;
    dst[o + 1] = bestG;
    dst[o + 2] = bestB;
    dst[o + 3] = src[o + 3]!;
  }
}

// ---------------------------------------------------------------------------
// Clouds / Difference Clouds
// ---------------------------------------------------------------------------

function hash2D(x: number, y: number, seed: number): number {
  let h = seed * 374761393 + x * 668265263 + y * 1274126177;
  h = (h ^ (h >> 13)) * 1274126177;
  return ((h ^ (h >> 16)) >>> 0) / 4294967296;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function valueNoise2D(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const sx = smoothstep(fx);
  const sy = smoothstep(fy);
  const n00 = hash2D(ix, iy, seed);
  const n10 = hash2D(ix + 1, iy, seed);
  const n01 = hash2D(ix, iy + 1, seed);
  const n11 = hash2D(ix + 1, iy + 1, seed);
  const nx0 = n00 + (n10 - n00) * sx;
  const nx1 = n01 + (n11 - n01) * sx;
  return nx0 + (nx1 - nx0) * sy;
}

function applyClouds(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  seed: number,
  diff: boolean,
): void {
  if (w === 0 || h === 0) return;
  const n = w * h;
  const maxAmplitude = 1 + 0.5 + 0.25;
  for (let i = 0; i < n; i++) {
    const y = Math.floor(i / w);
    const x = i % w;
    let value = 0;
    let amplitude = 1;
    for (let octave = 0; octave < 3; octave++) {
      const freq = 1 << octave;
      const nx = (x / w) * freq;
      const ny = (y / h) * freq;
      value += valueNoise2D(nx, ny, seed) * amplitude;
      amplitude *= 0.5;
    }
    value = Math.max(0, Math.min(1, value / maxAmplitude)) * 255;
    const gray = Math.round(value);
    const o = i * 4;
    if (diff) {
      dst[o] = clamp8(Math.abs(src[o]! - gray));
      dst[o + 1] = clamp8(Math.abs(src[o + 1]! - gray));
      dst[o + 2] = clamp8(Math.abs(src[o + 2]! - gray));
      dst[o + 3] = src[o + 3]!;
    } else {
      dst[o] = gray;
      dst[o + 1] = gray;
      dst[o + 2] = gray;
      dst[o + 3] = 255;
    }
  }
}

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const UtilitySuite: Filter<UtilityParams> = {
  id: "utility-suite",
  displayName: "Filters",
  defaultParams: {
    mode: "high-pass",
    radius: 10,
    offsetX: 0,
    offsetY: 0,
    fill: "wrap",
    bgColor: { r: 0, g: 0, b: 0 },
    seed: 0,
  },

  apply(
    input: PixelBuffer,
    params: UtilityParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    switch (params.mode) {
      case "high-pass":
        applyHighPass(src, dst, w, h, params.radius);
        break;
      case "offset":
        applyOffset(src, dst, w, h, params.offsetX, params.offsetY, params.fill, params.bgColor);
        break;
      case "minimum":
        applyMinMax(src, dst, w, h, params.radius, true);
        break;
      case "maximum":
        applyMinMax(src, dst, w, h, params.radius, false);
        break;
      case "clouds":
        applyClouds(src, dst, w, h, params.seed, false);
        break;
      case "difference-clouds":
        applyClouds(src, dst, w, h, params.seed, true);
        break;
    }

    return out;
  },
};
